import { CurrencyCode } from '@buggy/shared'
import dayjs from 'dayjs'
import { v4 } from 'uuid'
import { KOLAnalyze } from '@/api/workspaces-api'
import mockAnalyzeImage1Src from '@/assets/images/kol/mock-analyze1.png'
import { CountryCode } from '@/constants/country-code'
import {
  fixedTimes,
  mockFixedEngagementAnalysisDataCount,
  mockFixedPostFrequencyDataCount,
  mockFixedTrackingAnalysisCount,
  mockFixedViewAnalysisDataBase,
} from '@/constants/kol-detail/fixed-mock-base-data'
import { mockEmptyPostList } from '@/constants/mock-data/ai-search'
import {
  makeKolInfo1,
  makeKolInfo2,
  makeKolInfo3,
  unimportantAttrsSnakeCased,
} from '@/constants/mock-data/kol-info'
import { mockMentionedBrands } from '@/constants/mock-data/mentioned-brands'
import { mockMentionedKolList } from '@/constants/mock-data/mentioned-kols'
import { SupportedLanguages } from '@/i18n/config'
import { Audience, Follower } from '@/store/kol-detail'
import { CampaignKolPaymentStatus } from '@/types/mock-api-types'
import {
  FetchCustomizedFieldsResponse,
  FetchKolContactInfoResponse,
  FetchKolFootprintResponse,
  FetchKolManagementResponse,
  Footprint,
  type DirectoryKol,
} from '@/types/schema/directory/kol-management'
import { PlatformShortcode } from '@/utils/convert-platform'
import { Potential } from '@/utils/detail/potential'

export const mockFixedPostFrequencyData: KOLAnalyze[] =
  mockFixedPostFrequencyDataCount
    .map((data, index) => ({
      ...data,
      url: '',
      likeCount: 0,
      interactiveRate: 0,
      commentCount: 0,
      shareCount: 0,
      titleContent: '***************************',
      viewRate: 0,
      isShort: false,
      isAdvertorial: true,
      crawledId: '',
      postId: '',
      igPostId: '',
      thumbnail: mockAnalyzeImage1Src,
      publishedAt:
        dayjs().subtract(index, 'day').format('YYYY-MM-DD') +
        `T${fixedTimes[index % fixedTimes.length]}`,
    }))
    .reverse()

export const mockFixedViewAnalysisData: KOLAnalyze[] =
  mockFixedViewAnalysisDataBase
    .map((data, index) => ({
      ...data,
      url: '',
      likeCount: 0,
      interactiveCount: 0,
      interactiveRate: 0,
      commentCount: 0,
      shareCount: 0,
      crawledId: '',
      postId: '',
      igPostId: '',
      titleContent: '***************************',
      viewRate: 0,
      isVideo: true,
      isShort: false,
      publishedAt: dayjs().subtract(index, 'week').format('YYYY-MM-DD'),
      isAdvertorial: true,
      thumbnail: mockAnalyzeImage1Src,
    }))
    .reverse()

export const mockFixedEngagementAnalysisData: KOLAnalyze[] =
  mockFixedEngagementAnalysisDataCount
    .map((data, index) => ({
      ...data,
      url: '',
      interactiveRate: 0,
      crawledId: '',
      postId: '',
      igPostId: '',
      titleContent: '***************************',
      viewCount: 0,
      viewRate: 0,
      isVideo: false,
      isLive: false,
      isShort: false,
      publishedAt: dayjs().subtract(index, 'week').format('YYYY-MM-DD'),
      isAdvertorial: true,
      thumbnail: mockAnalyzeImage1Src,
    }))
    .reverse()

export const mockFixedTrackingAnalysisData: Follower[] =
  mockFixedTrackingAnalysisCount.map((data, index) => ({
    ...data,
    createdAt: dayjs().subtract(index, 'week').format('YYYY/MM/DD'),
  }))

export const mockAudienceData: Audience = {
  ages: [
    {
      code: '13-17',
      weight: 0.05,
    },
    {
      code: '18-24',
      weight: 0.22,
    },
    {
      code: '25-34',
      weight: 0.45,
    },
    {
      code: '35-44',
      weight: 0.18,
    },
    {
      code: '45-64',
      weight: 0.08,
    },
    {
      code: '65-',
      weight: 0.02,
    },
  ],
  gender: {
    male: 0.4386,
    female: 0.5614,
  },
  gendersPerAges: [
    {
      code: '13-17',
      male: 0.02,
      female: 0.03,
    },
    {
      code: '18-24',
      male: 0.1,
      female: 0.12,
    },
    {
      code: '25-34',
      male: 0.2,
      female: 0.25,
    },
    {
      code: '35-44',
      male: 0.1,
      female: 0.08,
    },
    {
      code: '45-64',
      male: 0.06,
      female: 0.02,
    },
    {
      code: '65-',
      male: 0.01,
      female: 0.01,
    },
  ],
  geo: {
    cities: [
      {
        country: {
          code: 'TW',
          name: 'Taiwan',
        },
        name: 'New Taipei City',
        weight: 0.153,
      },
      {
        country: {
          code: 'TW',
          name: 'Taiwan',
        },
        name: 'Taipei City',
        weight: 0.143,
      },
      {
        country: {
          code: 'TW',
          name: 'Taiwan',
        },
        name: 'Taichung City',
        weight: 0.091,
      },
      {
        country: {
          code: 'TW',
          name: 'Taiwan',
        },
        name: 'Kaohsiung City',
        weight: 0.081,
      },
      {
        country: {
          code: 'HK',
          name: 'Hong Kong',
        },
        name: 'Hong Kong',
        weight: 0.078,
      },
      {
        country: {
          code: 'JP',
          name: 'Japan',
        },
        name: 'Osaka',
        weight: 0.065,
      },
    ],
    countries: [
      {
        code: 'TW',
        name: 'Taiwan',
        weight: 0.669,
      },
      {
        code: 'HK',
        name: 'Hong Kong',
        weight: 0.078,
      },
      {
        code: 'JP',
        name: 'Japan',
        weight: 0.04,
      },
      {
        code: 'US',
        name: 'United States of America',
        weight: 0.025,
      },
      {
        code: 'MY',
        name: 'Malaysia',
        weight: 0.022,
      },
      {
        code: 'UA',
        name: 'Ukraine',
        weight: 0.021,
      },
    ],
  },
  mainAge: {
    code: '25-34',
    weight: 0.76,
  },
  mainCountry: {
    code: 'TW',
    name: 'Taiwan',
    weight: 0.76,
  },
  mainGender: {
    code: 'female',
    weight: 0.76,
  },
  authenticity: {
    real: '0.85',
    suspicious: '0.15',
  },
  updatedAt: '2022-07-22T02:08:32Z',
}

const mockKolFootprintDataCollectionFolder: Footprint[] = [
  {
    id: 'E6URdZIBqKfUlYaVYHSI8',
    user_id: 4,
    user_name: 'Jennifer Chang',
    created_at: '2021/10/05 11:00:35',
    footprint_resource_name: 'Kolr 夏季招募',
    footprint_resource_status: 'new',
    footprint_resource_type: 'collection-folder',
    footprint_resource_id: 3,
    meta_status: null,
  },
  {
    id: 'E6URdZIBqKfUlYaVYHSI17',
    user_id: 5,
    user_name: 'Jennifer Chang',
    created_at: '2021/10/05 11:00:35',
    footprint_resource_name: 'Kolr 夏季招募',
    footprint_resource_status: 'remove',
    footprint_resource_type: 'collection-folder',
    footprint_resource_id: 4,
    meta_status: null,
  },
]

const mockKolFootprintDataCampaign: Footprint[] = [
  {
    id: 'E6URdZIBqKfUlYaVYHSI1',
    user_id: 6,
    user_name: 'Tracy Lu',
    created_at: '2021-10-05T11:00:35Z',
    footprint_resource_name: 'Kolr 夏季 IG 圖文',
    footprint_resource_status: 'ended',
    footprint_resource_type: 'campaign',
    footprint_resource_id: 5,
    meta_status: null,
  },
  {
    id: 'E6URdZIBqKfUlYaVYHSI2',
    user_id: 7,
    user_name: 'Tracy Lu',
    created_at: '2021-10-05T11:00:35Z',
    footprint_resource_name: 'Kolr 夏季 IG 圖文',
    footprint_resource_status: 'update',
    meta_status: CampaignKolPaymentStatus.Paid,
    footprint_resource_type: 'campaign',
    footprint_resource_id: 6,
  },
  {
    id: 'E6URdZIBqKfUlYaVYHSI3',
    user_id: 8,
    user_name: 'Tracy Lu',
    created_at: '2021-10-05T11:00:35Z',
    footprint_resource_name: 'Kolr 夏季 IG 圖文',
    footprint_resource_status: 'in-process',
    footprint_resource_type: 'campaign',
    meta_status: null,
    footprint_resource_id: 7,
  },
  {
    id: 'E6URdZIBqKfUlYaVYHSI4',
    user_id: 9,
    user_name: 'Tracy Lu',
    created_at: '2021-10-05T11:00:35Z',
    footprint_resource_name: 'Kolr 夏季 IG 圖文',
    footprint_resource_status: 'new',
    footprint_resource_type: 'campaign',
    meta_status: null,
    footprint_resource_id: 8,
  },
  {
    id: 'E6URdZIBqKfUlYaVYHSI5',
    user_id: 9,
    user_name: 'Tracy Lu',
    created_at: '2021-10-05T11:00:35Z',
    footprint_resource_name: 'Kolr 夏季 IG 圖文',
    footprint_resource_status: 'cancel-collaboration',
    footprint_resource_type: 'campaign',
    meta_status: null,
    footprint_resource_id: 8,
  },
  {
    id: 'E6URdZIBqKfUlYaVYHSI6',
    user_id: 9,
    user_name: 'Tracy Lu',
    created_at: '2021-10-05T11:00:35Z',
    footprint_resource_name: 'Kolr 夏季 IG 圖文',
    footprint_resource_status: 'remove',
    footprint_resource_type: 'campaign',
    meta_status: null,
    footprint_resource_id: 8,
  },
]

const makeMockDirectoryKol = (locale?: SupportedLanguages): DirectoryKol => {
  const kolInfo = makeKolInfo1(locale)

  return {
    ...unimportantAttrsSnakeCased,
    kol_management_status: 'created',
    is_lock: false,
    is_expired: false,
    is_verified: kolInfo.verified,
    kol_uuid: '6b8d9ac5-c1b1-494b-bb1c-1f2bc9730131',
    names: [kolInfo.name, ...kolInfo.subName],
    potential: Potential.Normal,
    is_radar_star: kolInfo.isRadarStar,
    country_code: CountryCode.TW,
    gender: 'Male',
    customized_tags: kolInfo.customizedTagsNames,
    follow_count: kolInfo.follower,
    birthday: '1995-11-11',
    profession: '護理師',
    include_collection_count: 3,
    include_campaign_count: 3,
    include_deep_report_count: 3,
    footprint: mockKolFootprintDataCampaign[0],
    customized_labels: [
      {
        id: -1,
        name: 'Kolr 夏季 IG 圖文',
      },
      {
        id: -2,
        name: 'Kolr 夏季 IG 圖文',
      },
      {
        id: 1,
        name: '互惠',
      },
    ],
    languages: [
      {
        name: 'zh-hant',
        weight: 0.852,
      },
      {
        name: 'en',
        weight: 0.148,
      },
    ],
    collaboration_record: {
      id: 1,
      name: 'Kolr 夏季 IG 圖文',
      collaboration_record_type: 'campaign',
      start_at: '2021-10-05T11:00:35Z',
      budget: 10000,
    },
    contact_infos: [
      {
        type: 'email',
        value: 'test@test.com',
        directory_contact_id: 0,
      },
      {
        type: 'phone',
        value: '0987654321',
        directory_contact_id: 0,
      },
      {
        type: 'line',
        value: 'thisIsLine',
        directory_contact_id: 0,
      },
    ],
    links: [
      {
        type: PlatformShortcode.Facebook,
        url: 'https://www.facebook.com/profile.php?id=100000000000000',
        name_list: ['奇軒Tricking', '@73_tricking'],
        description: kolInfo.description,
      },
    ],
    brand_asset_hub_customized_fields: [
      {
        id: 16,
        type: '膚質',
        value: '油性肌',
        field_type: 'asset-hub',
      },
    ],
    platform_type_statistics: [
      {
        type: PlatformShortcode.Instagram,
        score_3m: 85,
        score_6m: 95,
        short_engagement_rate_3m: 0.0001625128564749883,
        short_engagement_rate_6m: 0.0001625128564749883,
        avg_short_engagement_count_3m: 1000,
        avg_short_engagement_count_6m: 1000,
        avg_short_like_count_3m: 1000,
        avg_short_like_count_6m: 1000,
        avg_short_comment_count_3m: 1000,
        avg_short_comment_count_6m: 1000,
        avg_short_play_count_3m: 1000,
        avg_short_play_count_6m: 1000,
        short_play_rate_3m: 0.0001625128564749883,
        short_play_rate_6m: 0.0001625128564749883,
        engagement_rate_3m: 0.0001625128564749883,
        engagement_rate_6m: 0.0001625128564749883,
        avg_engagement_count_3m: 1000,
        avg_engagement_count_6m: 1000,
        avg_share_count_3m: null,
        avg_share_count_6m: null,
        avg_like_count_3m: 1000,
        avg_like_count_6m: 1000,
        avg_comment_count_3m: 1000,
        avg_comment_count_6m: 1000,
        avg_view_count_3m: 1000,
        avg_view_count_6m: 1000,
        post_count_3m: 10,
        post_count_6m: 10,
        view_rate_3m: null,
        view_rate_6m: null,
        customized_property_statistic_3m: kolInfo.customizedTagsNames,
        customized_property_statistic_6m: kolInfo.customizedTagsNames,
        audience_main_country: {
          code: CountryCode.TW,
          weight: 0.46708542,
        },
        audience_main_age: {
          code: '20-24',
          weight: 0.46708542,
        },
        audience_main_gender: {
          code: 'male',
          weight: 0.46708542,
        },
        follow_count: kolInfo.follower,
        post_frequency_3m: 1,
        post_frequency_6m: 1,
        sponsor_post_ratio_3m: 0.5,
        sponsor_post_ratio_6m: 0.5,
        post_word_of_mouth_volume_3m: 1000,
        post_word_of_mouth_volume_6m: 1000,
        min_image_estimated_reach_3m: 12300,
        max_image_estimated_reach_3m: 22200,
        min_image_estimated_reach_6m: 12300,
        max_image_estimated_reach_6m: 22200,
        video_word_of_mouth_volume_3m: 1000,
        video_word_of_mouth_volume_6m: 1000,
        min_video_estimated_reach_3m: 12300,
        max_video_estimated_reach_3m: 22200,
        min_video_estimated_reach_6m: 12300,
        max_video_estimated_reach_6m: 22200,
        follow_count_growth_3m: 1000,
        follow_count_growth_6m: 1000,
        follow_count_growth_rate_3m: 0.5,
        follow_count_growth_rate_6m: 0.5,
        live_view_count_3m: null,
        live_view_count_6m: null,
        live_view_rate_3m: null,
        live_view_rate_6m: null,
        follower_quality: 0.5,
        min_short_estimated_reach_3m: null,
        max_short_estimated_reach_3m: null,
        min_short_estimated_reach_6m: null,
        max_short_estimated_reach_6m: null,
        potential: Potential.High,
        like_comment_ratio_3m: 0.5,
        like_comment_ratio_6m: 0.5,
        quotations: {
          post: {
            min_price: 500,
            max_price: 1000,
            currency: CurrencyCode.TWD,
          },
          video: {
            min_price: 1200,
            max_price: 12000,
            currency: CurrencyCode.TWD,
          },
        },
      },
      {
        type: PlatformShortcode.Facebook,
        score_3m: 85,
        score_6m: 95,
        short_engagement_rate_3m: null,
        short_engagement_rate_6m: null,
        avg_short_engagement_count_3m: null,
        avg_short_engagement_count_6m: null,
        avg_short_like_count_3m: null,
        avg_short_like_count_6m: null,
        avg_short_comment_count_3m: null,
        avg_short_comment_count_6m: null,
        engagement_rate_3m: 0.0001625128564749883,
        engagement_rate_6m: 0.0001625128564749883,
        avg_engagement_count_3m: null,
        avg_engagement_count_6m: null,
        avg_share_count_3m: 10000,
        avg_share_count_6m: 10000,
        avg_like_count_3m: 1000,
        avg_like_count_6m: 1000,
        avg_comment_count_3m: 1000,
        avg_comment_count_6m: 1000,
        avg_view_count_3m: 1000,
        avg_view_count_6m: 1000,
        post_count_3m: 10,
        post_count_6m: 10,
        view_rate_3m: 0.0001625128564749883,
        view_rate_6m: 0.0001625128564749883,
        customized_property_statistic_3m: kolInfo.customizedTagsNames,
        customized_property_statistic_6m: kolInfo.customizedTagsNames,
        audience_main_country: null,
        audience_main_age: {
          code: '20-24',
          weight: 0.46708542,
        },
        audience_main_gender: {
          code: 'female',
          weight: 0.46708542,
        },
        follow_count: kolInfo.follower,
        post_frequency_3m: 1,
        post_frequency_6m: 1,
        sponsor_post_ratio_3m: 0.5,
        sponsor_post_ratio_6m: 0.5,
        post_word_of_mouth_volume_3m: 1000,
        post_word_of_mouth_volume_6m: 1000,
        min_image_estimated_reach_3m: 12300,
        max_image_estimated_reach_3m: 22200,
        min_image_estimated_reach_6m: 12300,
        max_image_estimated_reach_6m: 22200,
        video_word_of_mouth_volume_3m: 1000,
        video_word_of_mouth_volume_6m: 1000,
        min_video_estimated_reach_3m: 12300,
        max_video_estimated_reach_3m: 22200,
        min_video_estimated_reach_6m: 12300,
        max_video_estimated_reach_6m: 22200,
        follow_count_growth_3m: 1000,
        follow_count_growth_6m: 1000,
        follow_count_growth_rate_3m: 0.5,
        follow_count_growth_rate_6m: 0.5,
        live_view_count_3m: 1000,
        live_view_count_6m: 1000,
        live_view_rate_3m: 0.5,
        live_view_rate_6m: 0.5,
        follower_quality: null,
        min_short_estimated_reach_3m: null,
        max_short_estimated_reach_3m: null,
        min_short_estimated_reach_6m: null,
        max_short_estimated_reach_6m: null,
        potential: Potential.Decline,
        like_comment_ratio_3m: 0.5,
        like_comment_ratio_6m: 0.5,
        quotations: {
          post: {
            min_price: 500,
            max_price: 1000,
            currency: CurrencyCode.TWD,
          },
          video: {
            min_price: 1200,
            max_price: 12000,
            currency: CurrencyCode.TWD,
          },
        },
      },
      {
        type: PlatformShortcode.YouTube,
        score_3m: 85,
        score_6m: 95,
        short_engagement_rate_3m: 0.0001625128564749883,
        short_engagement_rate_6m: 0.0001625128564749883,
        avg_short_engagement_count_3m: 1000,
        avg_short_engagement_count_6m: 1000,
        avg_short_like_count_3m: 1000,
        avg_short_like_count_6m: 1000,
        avg_short_comment_count_3m: 1000,
        avg_short_comment_count_6m: 1000,
        avg_short_view_count_3m: 1000,
        avg_short_view_count_6m: 1000,
        short_view_rate_3m: 0.0001625128564749883,
        short_view_rate_6m: 0.0001625128564749883,
        engagement_rate_3m: 0.0001625128564749883,
        engagement_rate_6m: 0.0001625128564749883,
        avg_engagement_count_3m: 1000,
        avg_engagement_count_6m: 1000,
        avg_share_count_3m: null,
        avg_share_count_6m: null,
        avg_like_count_3m: 1000,
        avg_like_count_6m: 1000,
        avg_comment_count_3m: 1000,
        avg_comment_count_6m: 1000,
        avg_view_count_3m: 1000,
        avg_view_count_6m: 1000,
        post_count_3m: 10,
        post_count_6m: 10,
        view_rate_3m: 0.0001625128564749883,
        view_rate_6m: 0.0001625128564749883,
        customized_property_statistic_3m: kolInfo.customizedTagsNames,
        customized_property_statistic_6m: kolInfo.customizedTagsNames,
        audience_main_country: {
          code: CountryCode.TW,
          weight: 0.46708542,
        },
        audience_main_age: {
          code: '20-24',
          weight: 0.46708542,
        },
        audience_main_gender: {
          code: 'female',
          weight: 0.46708542,
        },
        follow_count: kolInfo.follower,
        post_frequency_3m: 1,
        post_frequency_6m: 1,
        sponsor_post_ratio_3m: 0.5,
        sponsor_post_ratio_6m: 0.5,
        post_word_of_mouth_volume_3m: 1000,
        post_word_of_mouth_volume_6m: 1000,
        min_image_estimated_reach_3m: null,
        max_image_estimated_reach_3m: null,
        min_image_estimated_reach_6m: null,
        max_image_estimated_reach_6m: null,
        video_word_of_mouth_volume_3m: 1000,
        video_word_of_mouth_volume_6m: 1000,
        min_video_estimated_reach_3m: 12300,
        max_video_estimated_reach_3m: 22200,
        min_video_estimated_reach_6m: 12300,
        max_video_estimated_reach_6m: 22200,
        follow_count_growth_3m: 1000,
        follow_count_growth_6m: 1000,
        follow_count_growth_rate_3m: 0.5,
        follow_count_growth_rate_6m: 0.5,
        live_view_count_3m: null,
        live_view_count_6m: null,
        live_view_rate_3m: 0.5,
        live_view_rate_6m: 0.5,
        follower_quality: null,
        min_short_estimated_reach_3m: 12300,
        max_short_estimated_reach_3m: 22200,
        min_short_estimated_reach_6m: 12300,
        max_short_estimated_reach_6m: 22200,
        potential: Potential.Normal,
        like_comment_ratio_3m: 0.5,
        like_comment_ratio_6m: 0.5,
        quotations: {
          post: {
            min_price: 500,
            max_price: 1000,
            currency: CurrencyCode.TWD,
          },
          video: {
            min_price: 1200,
            max_price: 12000,
            currency: CurrencyCode.TWD,
          },
        },
      },
      {
        type: PlatformShortcode.TikTok,
        score_3m: 85,
        score_6m: 95,
        short_engagement_rate_3m: 0.0001625128564749883,
        short_engagement_rate_6m: 0.0001625128564749883,
        avg_short_engagement_count_3m: 1000,
        avg_short_engagement_count_6m: 1000,
        avg_short_like_count_3m: 1000,
        avg_short_like_count_6m: 1000,
        avg_short_comment_count_3m: 1000,
        avg_short_comment_count_6m: 1000,
        avg_short_play_count_3m: 1000,
        avg_short_play_count_6m: 1000,
        short_play_rate_3m: 0.0001625128564749883,
        short_play_rate_6m: 0.0001625128564749883,
        engagement_rate_3m: 0.0001625128564749883,
        engagement_rate_6m: 0.0001625128564749883,
        avg_engagement_count_3m: 1000,
        avg_engagement_count_6m: 1000,
        avg_share_count_3m: null,
        avg_share_count_6m: null,
        avg_like_count_3m: 1000,
        avg_like_count_6m: 1000,
        avg_comment_count_3m: 1000,
        avg_comment_count_6m: 1000,
        avg_view_count_3m: 1000,
        avg_view_count_6m: 1000,
        post_count_3m: 10,
        post_count_6m: 10,
        view_rate_3m: 0.0001625128564749883,
        view_rate_6m: 0.0001625128564749883,
        customized_property_statistic_3m: [],
        customized_property_statistic_6m: [],
        audience_main_country: null,
        audience_main_age: {
          code: '20-24',
          weight: 0.46708542,
        },
        audience_main_gender: {
          code: 'female',
          weight: 0.46708542,
        },
        follow_count: kolInfo.follower,
        post_frequency_3m: 1,
        post_frequency_6m: 1,
        sponsor_post_ratio_3m: null,
        sponsor_post_ratio_6m: null,
        post_word_of_mouth_volume_3m: null,
        post_word_of_mouth_volume_6m: null,
        min_image_estimated_reach_3m: null,
        max_image_estimated_reach_3m: null,
        min_image_estimated_reach_6m: null,
        max_image_estimated_reach_6m: null,
        video_word_of_mouth_volume_3m: 1000,
        video_word_of_mouth_volume_6m: 1000,
        min_video_estimated_reach_3m: null,
        max_video_estimated_reach_3m: null,
        min_video_estimated_reach_6m: null,
        max_video_estimated_reach_6m: null,
        follow_count_growth_3m: 1000,
        follow_count_growth_6m: 1000,
        follow_count_growth_rate_3m: 0.5,
        follow_count_growth_rate_6m: 0.5,
        live_view_count_3m: null,
        live_view_count_6m: null,
        live_view_rate_3m: 0.5,
        live_view_rate_6m: 0.5,
        follower_quality: null,
        min_short_estimated_reach_3m: null,
        max_short_estimated_reach_3m: null,
        min_short_estimated_reach_6m: null,
        max_short_estimated_reach_6m: null,
        potential: null,
        like_comment_ratio_3m: null,
        like_comment_ratio_6m: null,
        quotations: null,
      },
      {
        type: PlatformShortcode.Twitter,
        score_3m: 85,
        score_6m: 95,
        short_engagement_rate_3m: 0.0001625128564749883,
        short_engagement_rate_6m: 0.0001625128564749883,
        avg_short_engagement_count_3m: 1000,
        avg_short_engagement_count_6m: 1000,
        avg_short_like_count_3m: 1000,
        avg_short_like_count_6m: 1000,
        avg_short_comment_count_3m: 1000,
        avg_short_comment_count_6m: 1000,
        engagement_rate_3m: 0.0001625128564749883,
        engagement_rate_6m: 0.0001625128564749883,
        avg_engagement_count_3m: 1000,
        avg_engagement_count_6m: 1000,
        avg_share_count_3m: null,
        avg_share_count_6m: null,
        avg_like_count_3m: 1000,
        avg_like_count_6m: 1000,
        avg_comment_count_3m: 1000,
        avg_comment_count_6m: 1000,
        avg_view_count_3m: 1000,
        avg_view_count_6m: 1000,
        post_count_3m: 10,
        post_count_6m: 10,
        view_rate_3m: null,
        view_rate_6m: null,
        customized_property_statistic_3m: [],
        customized_property_statistic_6m: [],
        audience_main_country: null,
        audience_main_age: null,
        audience_main_gender: null,
        follow_count: kolInfo.follower,
        post_frequency_3m: 1,
        post_frequency_6m: 1,
        sponsor_post_ratio_3m: null,
        sponsor_post_ratio_6m: null,
        post_word_of_mouth_volume_3m: null,
        post_word_of_mouth_volume_6m: null,
        min_image_estimated_reach_3m: null,
        max_image_estimated_reach_3m: null,
        min_image_estimated_reach_6m: null,
        max_image_estimated_reach_6m: null,
        video_word_of_mouth_volume_3m: null,
        video_word_of_mouth_volume_6m: null,
        min_video_estimated_reach_3m: null,
        max_video_estimated_reach_3m: null,
        min_video_estimated_reach_6m: null,
        max_video_estimated_reach_6m: null,
        follow_count_growth_3m: 1000,
        follow_count_growth_6m: 1000,
        follow_count_growth_rate_3m: 0.5,
        follow_count_growth_rate_6m: 0.5,
        live_view_count_3m: null,
        live_view_count_6m: null,
        live_view_rate_3m: null,
        live_view_rate_6m: null,
        follower_quality: null,
        min_short_estimated_reach_3m: null,
        max_short_estimated_reach_3m: null,
        min_short_estimated_reach_6m: null,
        max_short_estimated_reach_6m: null,
        potential: null,
        like_comment_ratio_3m: null,
        like_comment_ratio_6m: null,
        quotations: null,
      },
    ],
    platform_type_latest_posts: [
      {
        type: PlatformShortcode.Instagram,
        posts: mockEmptyPostList.map((post) => ({
          ...post,
          post_id: v4(),
        })),
      },
      {
        type: PlatformShortcode.Facebook,
        posts: mockEmptyPostList.map((post) => ({
          ...post,
          post_id: v4(),
        })),
      },
      {
        type: PlatformShortcode.YouTube,
        posts: mockEmptyPostList.map((post) => ({
          ...post,
          post_id: v4(),
        })),
      },
      {
        type: PlatformShortcode.TikTok,
        posts: mockEmptyPostList.map((post) => ({
          ...post,
          post_id: v4(),
        })),
      },
    ],
    platform_type_related_kol_type_brands_3m: [
      {
        type: PlatformShortcode.Instagram,
        brands: mockMentionedBrands.map((brand) => ({
          uuid: v4(),
          name: brand.name,
          mention_count: brand.mentionCount,
        })),
      },
    ],
    platform_type_related_kol_type_brands_6m: [
      {
        type: PlatformShortcode.Instagram,
        brands: [
          {
            uuid: v4(),
            name: mockMentionedBrands[0].name,
            mention_count: mockMentionedBrands[0].mentionCount,
          },
        ],
      },
    ],
    platform_type_related_kol_type_persons_3m: [
      {
        type: PlatformShortcode.Instagram,
        uuids: mockMentionedKolList.map((kol) => kol.uuid),
      },
    ],
    platform_type_related_kol_type_persons_6m: [
      {
        type: PlatformShortcode.Instagram,
        uuids: mockMentionedKolList.map((kol) => kol.uuid),
      },
    ],
    platform_type_similar_kols: [
      {
        type: PlatformShortcode.Instagram,
        uuids: mockMentionedKolList.map((kol) => kol.uuid),
      },
    ],
  }
}

export const mockDirectoryKol1 = makeMockDirectoryKol()

const makeMockKolManagementResponse = (
  locale?: SupportedLanguages,
): FetchKolManagementResponse => {
  const kolInfo2 = makeKolInfo2(locale)
  const kolInfo3 = makeKolInfo3(locale)

  return {
    data: {
      kols: [
        mockDirectoryKol1,
        {
          ...unimportantAttrsSnakeCased,
          kol_management_status: 'created',
          is_lock: true,
          is_verified: true,
          kol_uuid: kolInfo2.id,
          names: [kolInfo2.name, ...kolInfo2.subName],
          potential: Potential.High,
          is_radar_star: kolInfo2.isRadarStar,
          country_code: CountryCode.TW,
          gender: 'Female',
          customized_tags: kolInfo2.customizedTagsNames,
          follow_count: kolInfo2.follower,
          birthday: null,
          profession: '護理師',
          include_collection_count: 11,
          include_campaign_count: 11,
          include_deep_report_count: 11,
          footprint: mockKolFootprintDataCampaign[1],
          customized_labels: [
            {
              id: 1,
              name: '互惠',
            },
          ],
          languages: [
            {
              name: 'zh-hant',
              weight: 0.148,
            },
          ],
          collaboration_record: {
            id: 2,
            name: '喜樂麵包_冷凍品系列_202406_KOL',
            collaboration_record_type: 'campaign',
            start_at: '2021-10-05T11:00:35Z',
            budget: 10000,
          },
          contact_infos: [
            {
              type: 'email',
              value: 'test@test.com',
              directory_contact_id: 0,
            },
          ],
          links: [
            {
              type: PlatformShortcode.Instagram,
              url: 'https://www.facebook.com/profile.php?id=100000000000000',
              name_list: ['奇軒Tricking', '@73_tricking'],
              description:
                'IKEA宜家家居粉絲團和你分享居家佈置靈感、分店優惠訊息以及最新家具家飾！\nIKEA商品只有在IKEA官網電子商務平台及分店進行販售，並未與任何第三方在店外或官網外做任何合作。\n\n\n',
            },
            {
              type: PlatformShortcode.TikTok,
              url: 'https://www.facebook.com/profile.php?id=100000000000000',
              name_list: ['奇軒Tricking', '@73_tricking'],
              description:
                'IKEA宜家家居粉絲團和你分享居家佈置靈感、分店優惠訊息以及最新家具家飾！\nIKEA商品只有在IKEA官網電子商務平台及分店進行販售，並未與任何第三方在店外或官網外做任何合作。\n\n\n',
            },
          ],
          brand_asset_hub_customized_fields: [
            {
              id: 16,
              type: '膚質',
              value: '中性肌',
              field_type: 'asset-hub',
            },
          ],
          platform_type_statistics: [
            {
              type: PlatformShortcode.Instagram,
              score_3m: 85,
              score_6m: 95,
              short_engagement_rate_3m: null,
              short_engagement_rate_6m: null,
              avg_short_engagement_count_3m: null,
              avg_short_engagement_count_6m: null,
              avg_short_like_count_3m: null,
              avg_short_like_count_6m: null,
              avg_short_comment_count_3m: null,
              avg_short_comment_count_6m: null,
              avg_short_play_count_3m: null,
              avg_short_play_count_6m: null,
              short_play_rate_3m: null,
              short_play_rate_6m: null,
              engagement_rate_3m: null,
              engagement_rate_6m: null,
              avg_engagement_count_3m: null,
              avg_engagement_count_6m: null,
              avg_share_count_3m: null,
              avg_share_count_6m: null,
              avg_like_count_3m: null,
              avg_like_count_6m: null,
              avg_comment_count_3m: null,
              avg_comment_count_6m: null,
              avg_view_count_3m: null,
              avg_view_count_6m: null,
              post_count_3m: 10,
              post_count_6m: 10,
              view_rate_3m: null,
              view_rate_6m: null,
              customized_property_statistic_3m: [],
              customized_property_statistic_6m: [],
              audience_main_country: {
                code: CountryCode.TW,
                weight: 0.46708542,
              },
              audience_main_age: null,
              audience_main_gender: {
                code: 'female',
                weight: null,
              },
              follow_count: kolInfo2.follower,
              post_frequency_3m: 1,
              post_frequency_6m: 1,
              sponsor_post_ratio_3m: null,
              sponsor_post_ratio_6m: null,
              post_word_of_mouth_volume_3m: null,
              post_word_of_mouth_volume_6m: null,
              min_image_estimated_reach_3m: null,
              max_image_estimated_reach_3m: null,
              min_image_estimated_reach_6m: null,
              max_image_estimated_reach_6m: null,
              video_word_of_mouth_volume_3m: null,
              video_word_of_mouth_volume_6m: null,
              min_video_estimated_reach_3m: null,
              max_video_estimated_reach_3m: null,
              min_video_estimated_reach_6m: null,
              max_video_estimated_reach_6m: null,
              follow_count_growth_3m: null,
              follow_count_growth_6m: null,
              follow_count_growth_rate_3m: null,
              follow_count_growth_rate_6m: null,
              live_view_count_3m: null,
              live_view_count_6m: null,
              live_view_rate_3m: null,
              live_view_rate_6m: null,
              follower_quality: null,
              min_short_estimated_reach_3m: null,
              max_short_estimated_reach_3m: null,
              min_short_estimated_reach_6m: null,
              max_short_estimated_reach_6m: null,
              potential: null,
              like_comment_ratio_3m: null,
              like_comment_ratio_6m: null,
              quotations: {
                post: {
                  min_price: 350000,
                  max_price: null,
                  currency: CurrencyCode.TWD,
                },
                video: {
                  min_price: 350000,
                  max_price: null,
                  currency: CurrencyCode.TWD,
                },
              },
            },
            {
              type: PlatformShortcode.Facebook,
              score_3m: 85,
              score_6m: 95,
              short_engagement_rate_3m: null,
              short_engagement_rate_6m: null,
              avg_short_engagement_count_3m: null,
              avg_short_engagement_count_6m: null,
              avg_short_like_count_3m: null,
              avg_short_like_count_6m: null,
              avg_short_comment_count_3m: null,
              avg_short_comment_count_6m: null,
              engagement_rate_3m: null,
              engagement_rate_6m: null,
              avg_engagement_count_3m: null,
              avg_engagement_count_6m: null,
              avg_share_count_3m: null,
              avg_share_count_6m: null,
              avg_like_count_3m: null,
              avg_like_count_6m: null,
              avg_comment_count_3m: null,
              avg_comment_count_6m: null,
              avg_view_count_3m: null,
              avg_view_count_6m: null,
              post_count_3m: 10,
              post_count_6m: 10,
              view_rate_3m: null,
              view_rate_6m: null,
              customized_property_statistic_3m: [],
              customized_property_statistic_6m: [],
              audience_main_country: null,
              audience_main_age: null,
              audience_main_gender: {
                code: 'female',
                weight: null,
              },
              follow_count: kolInfo2.follower,
              post_frequency_3m: 1,
              post_frequency_6m: 1,
              sponsor_post_ratio_3m: null,
              sponsor_post_ratio_6m: null,
              post_word_of_mouth_volume_3m: null,
              post_word_of_mouth_volume_6m: null,
              min_image_estimated_reach_3m: null,
              max_image_estimated_reach_3m: null,
              min_image_estimated_reach_6m: null,
              max_image_estimated_reach_6m: null,
              video_word_of_mouth_volume_3m: null,
              video_word_of_mouth_volume_6m: null,
              min_video_estimated_reach_3m: null,
              max_video_estimated_reach_3m: null,
              min_video_estimated_reach_6m: null,
              max_video_estimated_reach_6m: null,
              follow_count_growth_3m: null,
              follow_count_growth_6m: null,
              follow_count_growth_rate_3m: null,
              follow_count_growth_rate_6m: null,
              live_view_count_3m: null,
              live_view_count_6m: null,
              live_view_rate_3m: null,
              live_view_rate_6m: null,
              follower_quality: null,
              min_short_estimated_reach_3m: null,
              max_short_estimated_reach_3m: null,
              min_short_estimated_reach_6m: null,
              max_short_estimated_reach_6m: null,
              potential: null,
              like_comment_ratio_3m: null,
              like_comment_ratio_6m: null,
              quotations: {
                post: {
                  min_price: 350000,
                  max_price: null,
                  currency: CurrencyCode.TWD,
                },
                video: {
                  min_price: 350000,
                  max_price: null,
                  currency: CurrencyCode.TWD,
                },
              },
            },
            {
              type: PlatformShortcode.YouTube,
              score_3m: 85,
              score_6m: 95,
              short_engagement_rate_3m: null,
              short_engagement_rate_6m: null,
              avg_short_engagement_count_3m: null,
              avg_short_engagement_count_6m: null,
              avg_short_like_count_3m: null,
              avg_short_like_count_6m: null,
              avg_short_comment_count_3m: null,
              avg_short_comment_count_6m: null,
              avg_short_view_count_3m: null,
              avg_short_view_count_6m: null,
              short_view_rate_3m: null,
              short_view_rate_6m: null,
              engagement_rate_3m: null,
              engagement_rate_6m: null,
              avg_engagement_count_3m: null,
              avg_engagement_count_6m: null,
              avg_share_count_3m: null,
              avg_share_count_6m: null,
              avg_like_count_3m: null,
              avg_like_count_6m: null,
              avg_comment_count_3m: null,
              avg_comment_count_6m: null,
              avg_view_count_3m: null,
              avg_view_count_6m: null,
              post_count_3m: 10,
              post_count_6m: 10,
              view_rate_3m: null,
              view_rate_6m: null,
              customized_property_statistic_3m: [],
              customized_property_statistic_6m: [],
              audience_main_country: null,
              audience_main_age: null,
              audience_main_gender: {
                code: 'male',
                weight: null,
              },
              follow_count: kolInfo2.follower,
              post_frequency_3m: 1,
              post_frequency_6m: 1,
              sponsor_post_ratio_3m: null,
              sponsor_post_ratio_6m: null,
              post_word_of_mouth_volume_3m: null,
              post_word_of_mouth_volume_6m: null,
              min_image_estimated_reach_3m: null,
              max_image_estimated_reach_3m: null,
              min_image_estimated_reach_6m: null,
              max_image_estimated_reach_6m: null,
              video_word_of_mouth_volume_3m: null,
              video_word_of_mouth_volume_6m: null,
              min_video_estimated_reach_3m: null,
              max_video_estimated_reach_3m: null,
              min_video_estimated_reach_6m: null,
              max_video_estimated_reach_6m: null,
              follow_count_growth_3m: null,
              follow_count_growth_6m: null,
              follow_count_growth_rate_3m: null,
              follow_count_growth_rate_6m: null,
              live_view_count_3m: null,
              live_view_count_6m: null,
              live_view_rate_3m: null,
              live_view_rate_6m: null,
              follower_quality: null,
              min_short_estimated_reach_3m: null,
              max_short_estimated_reach_3m: null,
              min_short_estimated_reach_6m: null,
              max_short_estimated_reach_6m: null,
              potential: null,
              like_comment_ratio_3m: null,
              like_comment_ratio_6m: null,
              quotations: {
                post: {
                  min_price: 350000,
                  max_price: null,
                  currency: CurrencyCode.TWD,
                },
                video: {
                  min_price: 350000,
                  max_price: null,
                  currency: CurrencyCode.TWD,
                },
              },
            },
          ],
          platform_type_latest_posts: [],
          platform_type_related_kol_type_brands_3m: [],
          platform_type_related_kol_type_brands_6m: [],
          platform_type_related_kol_type_persons_3m: [],
          platform_type_related_kol_type_persons_6m: [],
          platform_type_similar_kols: [
            {
              type: PlatformShortcode.Instagram,
              uuids: ['6b8d9ac5-c1b1-494b-bb1c-1f2bc9730131'],
            },
          ],
        },
        {
          ...unimportantAttrsSnakeCased,
          kol_management_status: 'created',
          is_lock: false,
          is_verified: kolInfo3.verified,
          kol_uuid: kolInfo3.id,
          names: [kolInfo3.name, ...kolInfo3.subName],
          potential: Potential.Normal,
          is_radar_star: kolInfo3.isRadarStar,
          country_code: null,
          gender: 'Unknown',
          customized_tags: kolInfo3.customizedTagsNames,
          follow_count: 1000000,
          birthday: '1995-11-11',
          profession: null,
          include_collection_count: null,
          include_campaign_count: null,
          include_deep_report_count: null,
          contact_infos: [],
          footprint: null,
          customized_labels: [],
          collaboration_record: null,
          languages: [],
          links: [
            {
              type: PlatformShortcode.Instagram,
              url: 'https://www.facebook.com/profile.php?id=100000000000000',
              name_list: ['奇軒Tricking', '@73_tricking'],
              description: kolInfo3.description,
            },
            {
              type: PlatformShortcode.YouTube,
              url: 'https://www.facebook.com/profile.php?id=100000000000000',
              name_list: ['奇軒Tricking', '@73_tricking'],
              description: kolInfo3.description,
            },
            {
              type: PlatformShortcode.Twitter,
              url: 'https://www.facebook.com/profile.php?id=100000000000000',
              name_list: ['奇軒Tricking', '@73_tricking'],
              description: kolInfo3.description,
            },
          ],
          brand_asset_hub_customized_fields: [
            {
              id: 16,
              type: '膚質',
              value: null,
              field_type: 'asset-hub',
            },
          ],
          platform_type_statistics: [
            {
              type: PlatformShortcode.Instagram,
              score_3m: null,
              score_6m: null,
              short_engagement_rate_3m: null,
              short_engagement_rate_6m: null,
              avg_short_engagement_count_3m: null,
              avg_short_engagement_count_6m: null,
              avg_short_like_count_3m: null,
              avg_short_like_count_6m: null,
              avg_short_comment_count_3m: null,
              avg_short_comment_count_6m: null,
              avg_short_play_count_3m: null,
              avg_short_play_count_6m: null,
              short_play_rate_3m: null,
              short_play_rate_6m: null,
              engagement_rate_3m: null,
              engagement_rate_6m: null,
              avg_engagement_count_3m: null,
              avg_engagement_count_6m: null,
              avg_share_count_3m: null,
              avg_share_count_6m: null,
              avg_like_count_3m: null,
              avg_like_count_6m: null,
              avg_comment_count_3m: null,
              avg_comment_count_6m: null,
              avg_view_count_3m: null,
              avg_view_count_6m: null,
              post_count_3m: 10,
              post_count_6m: 10,
              view_rate_3m: null,
              view_rate_6m: null,
              customized_property_statistic_3m: [],
              customized_property_statistic_6m: [],
              audience_main_country: null,
              audience_main_age: null,
              audience_main_gender: null,
              follow_count: kolInfo3.follower,
              post_frequency_3m: 1,
              post_frequency_6m: 1,
              sponsor_post_ratio_3m: null,
              sponsor_post_ratio_6m: null,
              post_word_of_mouth_volume_3m: null,
              post_word_of_mouth_volume_6m: null,
              min_image_estimated_reach_3m: null,
              max_image_estimated_reach_3m: null,
              min_image_estimated_reach_6m: null,
              max_image_estimated_reach_6m: null,
              video_word_of_mouth_volume_3m: null,
              video_word_of_mouth_volume_6m: null,
              min_video_estimated_reach_3m: null,
              max_video_estimated_reach_3m: null,
              min_video_estimated_reach_6m: null,
              max_video_estimated_reach_6m: null,
              follow_count_growth_3m: null,
              follow_count_growth_6m: null,
              follow_count_growth_rate_3m: null,
              follow_count_growth_rate_6m: null,
              live_view_count_3m: null,
              live_view_count_6m: null,
              live_view_rate_3m: null,
              live_view_rate_6m: null,
              follower_quality: null,
              min_short_estimated_reach_3m: null,
              max_short_estimated_reach_3m: null,
              min_short_estimated_reach_6m: null,
              max_short_estimated_reach_6m: null,
              potential: null,
              like_comment_ratio_3m: null,
              like_comment_ratio_6m: null,
              quotations: null,
            },
            {
              type: PlatformShortcode.Facebook,
              score_3m: null,
              score_6m: null,
              short_engagement_rate_3m: null,
              short_engagement_rate_6m: null,
              avg_short_engagement_count_3m: null,
              avg_short_engagement_count_6m: null,
              avg_short_like_count_3m: null,
              avg_short_like_count_6m: null,
              avg_short_comment_count_3m: null,
              avg_short_comment_count_6m: null,
              engagement_rate_3m: null,
              engagement_rate_6m: null,
              avg_engagement_count_3m: null,
              avg_engagement_count_6m: null,
              avg_share_count_3m: null,
              avg_share_count_6m: null,
              avg_like_count_3m: null,
              avg_like_count_6m: null,
              avg_comment_count_3m: null,
              avg_comment_count_6m: null,
              avg_view_count_3m: null,
              avg_view_count_6m: null,
              post_count_3m: 10,
              post_count_6m: 10,
              view_rate_3m: null,
              view_rate_6m: null,
              customized_property_statistic_3m: [],
              customized_property_statistic_6m: [],
              audience_main_country: null,
              audience_main_age: null,
              audience_main_gender: null,
              follow_count: kolInfo3.follower,
              post_frequency_3m: 1,
              post_frequency_6m: 1,
              sponsor_post_ratio_3m: null,
              sponsor_post_ratio_6m: null,
              post_word_of_mouth_volume_3m: null,
              post_word_of_mouth_volume_6m: null,
              min_image_estimated_reach_3m: null,
              max_image_estimated_reach_3m: null,
              min_image_estimated_reach_6m: null,
              max_image_estimated_reach_6m: null,
              video_word_of_mouth_volume_3m: null,
              video_word_of_mouth_volume_6m: null,
              min_video_estimated_reach_3m: null,
              max_video_estimated_reach_3m: null,
              min_video_estimated_reach_6m: null,
              max_video_estimated_reach_6m: null,
              follow_count_growth_3m: null,
              follow_count_growth_6m: null,
              follow_count_growth_rate_3m: null,
              follow_count_growth_rate_6m: null,
              live_view_count_3m: null,
              live_view_count_6m: null,
              live_view_rate_3m: null,
              live_view_rate_6m: null,
              follower_quality: null,
              min_short_estimated_reach_3m: null,
              max_short_estimated_reach_3m: null,
              min_short_estimated_reach_6m: null,
              max_short_estimated_reach_6m: null,
              potential: null,
              like_comment_ratio_3m: null,
              like_comment_ratio_6m: null,
              quotations: null,
            },
            {
              type: PlatformShortcode.YouTube,
              score_3m: null,
              score_6m: null,
              short_engagement_rate_3m: null,
              short_engagement_rate_6m: null,
              avg_short_engagement_count_3m: null,
              avg_short_engagement_count_6m: null,
              avg_short_like_count_3m: null,
              avg_short_like_count_6m: null,
              avg_short_comment_count_3m: null,
              avg_short_comment_count_6m: null,
              avg_short_view_count_3m: null,
              avg_short_view_count_6m: null,
              short_view_rate_3m: null,
              short_view_rate_6m: null,
              engagement_rate_3m: null,
              engagement_rate_6m: null,
              avg_engagement_count_3m: null,
              avg_engagement_count_6m: null,
              avg_share_count_3m: null,
              avg_share_count_6m: null,
              avg_like_count_3m: null,
              avg_like_count_6m: null,
              avg_comment_count_3m: null,
              avg_comment_count_6m: null,
              avg_view_count_3m: null,
              avg_view_count_6m: null,
              post_count_3m: 10,
              post_count_6m: 10,
              view_rate_3m: null,
              view_rate_6m: null,
              customized_property_statistic_3m: [],
              customized_property_statistic_6m: [],
              audience_main_country: null,
              audience_main_age: null,
              audience_main_gender: null,
              follow_count: kolInfo3.follower,
              post_frequency_3m: 1,
              post_frequency_6m: 1,
              sponsor_post_ratio_3m: null,
              sponsor_post_ratio_6m: null,
              post_word_of_mouth_volume_3m: null,
              post_word_of_mouth_volume_6m: null,
              min_image_estimated_reach_3m: null,
              max_image_estimated_reach_3m: null,
              min_image_estimated_reach_6m: null,
              max_image_estimated_reach_6m: null,
              video_word_of_mouth_volume_3m: null,
              video_word_of_mouth_volume_6m: null,
              min_video_estimated_reach_3m: null,
              max_video_estimated_reach_3m: null,
              min_video_estimated_reach_6m: null,
              max_video_estimated_reach_6m: null,
              follow_count_growth_3m: null,
              follow_count_growth_6m: null,
              follow_count_growth_rate_3m: null,
              follow_count_growth_rate_6m: null,
              live_view_count_3m: null,
              live_view_count_6m: null,
              live_view_rate_3m: null,
              live_view_rate_6m: null,
              follower_quality: null,
              min_short_estimated_reach_3m: null,
              max_short_estimated_reach_3m: null,
              min_short_estimated_reach_6m: null,
              max_short_estimated_reach_6m: null,
              potential: null,
              like_comment_ratio_3m: null,
              like_comment_ratio_6m: null,
              quotations: null,
            },
          ],
          platform_type_latest_posts: [],
          platform_type_related_kol_type_brands_3m: [],
          platform_type_related_kol_type_brands_6m: [],
          platform_type_related_kol_type_persons_3m: [],
          platform_type_related_kol_type_persons_6m: [],
          platform_type_similar_kols: [],
        },
      ],
      directory_kol_management_total: 100,
      total: 100,
      total_page: 10,
      next_page: v4(),
      previous_page: v4(),
    },
  }
}

export const mockKolManagementResponse = makeMockKolManagementResponse()

export const mockKolContactResponse: FetchKolContactInfoResponse = {
  data: {
    contact_infos: [
      {
        directory_contact_id: 0,
        type: 'email',
        resource_type: 'fb',
        value: 'public_email@ikala.ai',
      },
      {
        directory_contact_id: 0,
        type: 'email',
        resource_type: 'user',
        value: 'kol_mail@ikala.ai',
      },
      {
        directory_contact_id: 1,
        type: 'email',
        resource_type: 'directory',
        value: 'directory_email@ikala.ai',
        notes: 'this is a note',
      },
      {
        directory_contact_id: 0,
        type: 'phone',
        value: '09456456456',
      },
      {
        directory_contact_id: 2,
        type: 'phone',
        value: '0912123123',
      },
      {
        directory_contact_id: 3,
        type: 'line',
        value: 'thisIsLine',
      },
    ],
  },
}

const mockKolFootprintDataDeepReport: Footprint[] = [
  {
    id: 'E6URdZIBqKfUlYaVYHSI9',
    user_id: 9,
    user_name: 'Tracy Lu',
    created_at: '2021/10/05 11:00:35',
    footprint_resource_name: 'Kolr 年度活動',
    footprint_resource_status: 'new',
    footprint_resource_type: 'deep-report',
    meta_status: null,
    footprint_resource_id: 1547,
  },
  {
    id: 'E6URdZIBqKfUlYaVYHSI10',
    user_id: 9,
    user_name: 'Tracy Lu',
    created_at: '2021/10/05 11:00:35',
    footprint_resource_name: 'Kolr 年度活動',
    footprint_resource_status: 'remove',
    footprint_resource_type: 'deep-report',
    meta_status: null,
    footprint_resource_id: 1547,
  },
]

const mockKolFootprintDataOther: Footprint[] = [
  {
    id: 'E6URdZIBqKfUlYaVYHSI11',
    user_id: 1,
    user_name: 'Sharon',
    created_at: '2021/10/05 11:00:35',
    footprint_resource_name: null,
    footprint_resource_status: 'unlocked',
    footprint_resource_type: 'unlock',
    footprint_resource_id: 0,
    meta_status: null,
  },
  {
    id: 'E6URdZIBqKfUlYaVYHSI12',
    user_id: 3,
    user_name: 'Jennifer Chang',
    created_at: '2021/10/05 11:00:35',
    footprint_resource_status: 'remove',
    footprint_resource_type: 'asset-hub',
    footprint_resource_id: 2,
    meta_status: null,
    footprint_resource_name: null,
  },
  {
    id: 'E6URdZIBqKfUlYaVYHSI13',
    user_id: 2,
    user_name: 'Jennifer Chang',
    created_at: '2021/10/05 10:00:35',
    footprint_resource_status: 'new',
    footprint_resource_type: 'asset-hub',
    footprint_resource_id: 1,
    meta_status: null,
    footprint_resource_name: null,
  },
  {
    id: 'E6URdZIBqKfUlYaVYHSI14',
    user_id: 9,
    user_name: 'Tracy Lu',
    created_at: '2021/10/05 11:00:35',
    footprint_resource_name: 'Kolr 競品分析',
    footprint_resource_status: 'new',
    footprint_resource_type: 'competitor',
    meta_status: null,
    footprint_resource_id: 215,
  },
  {
    id: 'E6URdZIBqKfUlYaVYHSI15',
    user_id: 9,
    user_name: 'Tracy Lu',
    created_at: '2021/10/05 11:00:35',
    footprint_resource_name: 'Kolr 競品分析',
    footprint_resource_status: 'remove',
    footprint_resource_type: 'competitor',
    meta_status: null,
    footprint_resource_id: 215,
  },
  {
    id: 'E6URdZIBqKfUlYaVYHSI16',
    user_id: 9,
    user_name: 'Tracy Lu',
    created_at: '2021/10/05 11:00:35',
    footprint_resource_name: null,
    footprint_resource_status: 'new',
    footprint_resource_type: 'collaboration-detail-basic-info',
    meta_status: null,
    footprint_resource_id: 8,
  },
  {
    id: 'E6URdZIBqKfUlYaVYHSI17',
    user_id: 9,
    user_name: 'Tracy Lu',
    created_at: '2021/10/05 11:00:35',
    footprint_resource_name: null,
    footprint_resource_status: 'amend',
    footprint_resource_type: 'collaboration-detail-basic-info',
    footprint_resource_id: 8,
    meta_status: null,
  },
  {
    id: 'E6URdZIBqKfUlYaVYHSI18',
    user_id: 9,
    user_name: 'Tracy Lu',
    created_at: '2021/10/05 11:00:35',
    footprint_resource_name: null,
    footprint_resource_status: 'remove',
    footprint_resource_type: 'collaboration-detail-basic-info',
    footprint_resource_id: 8,
    meta_status: null,
  },
]

export const mockAllKolFootprintResponse: FetchKolFootprintResponse = {
  data: {
    footprints: [
      ...mockKolFootprintDataCollectionFolder,
      ...mockKolFootprintDataCampaign,
      ...mockKolFootprintDataDeepReport,
      ...mockKolFootprintDataOther,
    ],
    total: 18,
  },
}

export const mockKolFootprintDataCollectionFolderResponse: FetchKolFootprintResponse =
  {
    data: {
      footprints: mockKolFootprintDataCollectionFolder,
      total: mockKolFootprintDataCollectionFolder.length,
    },
  }

export const mockKolFootprintDataCampaignResponse: FetchKolFootprintResponse = {
  data: {
    footprints: mockKolFootprintDataCampaign,
    total: mockKolFootprintDataCampaign.length,
  },
}

export const mockKolFootprintDataDeepReportResponse: FetchKolFootprintResponse =
  {
    data: {
      footprints: mockKolFootprintDataDeepReport,
      total: mockKolFootprintDataDeepReport.length,
    },
  }

export const mockKolFootprintDataOtherResponse: FetchKolFootprintResponse = {
  data: {
    footprints: mockKolFootprintDataOther,
    total: mockKolFootprintDataOther.length,
  },
}

export const mockCustomizedFields: FetchCustomizedFieldsResponse = {
  data: [
    {
      id: 1,
      field_name: '髮色',
      field_value: '灰色',
      field_type: 'asset-hub',
      collection_folder_id: null,
    },
    {
      id: 2,
      field_name: '膚質',
      field_value: '油性肌',
      field_type: 'asset-hub',
      collection_folder_id: null,
    },
    {
      id: 3,
      field_name: '葷/素',
      field_value: null,
      field_type: 'asset-hub',
      collection_folder_id: null,
    },
  ],
}
